import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItemButton,
    Stack,
} from "@mui/material";
import {
    ArrowBackIos as BackIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon,
    Business as CompaniesIcon,
    Notes as NotesIcon,
    ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import QuoteService from "../services/Quote.service";

function LabelValue({ label, value }) {
    if (!value && value !== 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
                {label}
            </Typography>
            <Typography variant="body2">{value}</Typography>
        </Box>
    );
}

function FlagChip({ label }) {
    return <Chip label={label} size="small" color="primary" />;
}

function formatCurrency(value) {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function QuoteDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState("info");

    useEffect(() => {
        setLoading(true);
        setError("");
        QuoteService.get(id)
            .then((res) => setQuote(res.data))
            .catch(() => setError("Failed to load quote details."))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (panel) => (_, isOpen) => setExpanded(isOpen ? panel : false);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !quote) {
        return (
            <Box>
                <IconButton size="small" onClick={() => navigate(-1)} sx={{ mb: 1 }}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Alert severity="error">{error || "Quote not found."}</Alert>
            </Box>
        );
    }

    const customers = quote.customers_detail || [];

    return (
        <Box>
            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => navigate(-1)}>
                    <BackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                            {quote.number}
                        </Typography>
                        {quote.is_complete && <Chip label="Won" size="small" color="success" />}
                        {quote.is_archived && !quote.is_complete && (
                            <Chip label="Archived" size="small" />
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {quote.name}
                    </Typography>
                </Box>
            </Box>

            {/* ── Quote Info ── */}
            <Accordion
                expanded={expanded === "info"}
                onChange={toggle("info")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <InfoIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Quote Information
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <LabelValue label="Category" value={quote.project_category_detail?.name} />
                    <LabelValue label="Type" value={quote.project_type_detail?.name} />
                    <LabelValue label="Manager" value={quote.manager_detail ? `${quote.manager_detail.first_name} ${quote.manager_detail.last_name}`.trim() || quote.manager_detail.username : null} />
                    <LabelValue label="Price" value={formatCurrency(quote.price)} />
                    <LabelValue label="Probability" value={quote.probability != null ? `${quote.probability}%` : null} />
                    {quote.quote_due_date && (
                        <LabelValue label="Due Date" value={new Date(quote.quote_due_date).toLocaleDateString()} />
                    )}
                    {quote.expected_order_date && (
                        <LabelValue label="Expected Order Date" value={new Date(quote.expected_order_date).toLocaleDateString()} />
                    )}
                    {(quote.travel_job || quote.prevailing_rate || quote.union) && (
                        <Box sx={{ mt: 1.5 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                                sx={{ textTransform: "uppercase", letterSpacing: 0.4, display: "block", mb: 0.75 }}
                            >
                                Flags
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {quote.travel_job && <FlagChip label="Travel Job" />}
                                {quote.prevailing_rate && <FlagChip label="Prevailing Rate" />}
                                {quote.union && <FlagChip label="Union" />}
                            </Stack>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Customers ── */}
            <Accordion
                expanded={expanded === "customers"}
                onChange={toggle("customers")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CompaniesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Customers
                        </Typography>
                        {customers.length > 0 && (
                            <Chip label={customers.length} size="small" sx={{ height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                    {customers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No customers linked to this quote.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {customers.map((c, idx) => (
                                <React.Fragment key={c.id}>
                                    {idx > 0 && <Divider component="li" />}
                                    <ListItemButton
                                        onClick={() => navigate(`/crm/companies/${c.id}`)}
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                                            {c.name}
                                        </Typography>
                                        <ChevronRightIcon fontSize="small" color="action" />
                                    </ListItemButton>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* ── Notes ── */}
            <Accordion
                expanded={expanded === "notes"}
                onChange={toggle("notes")}
                disableGutters
                elevation={0}
                sx={{ border: 1, borderColor: "divider", mb: 1, "&:before": { display: "none" } }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "action.hover" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <NotesIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" fontWeight={600}>
                            Notes
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    {quote.notes ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {quote.notes}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No notes for this quote.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}

export default QuoteDetail;
